import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { jsonApi } from '@/lib/jsonApi';
import InlineEdit from '@/components/taxonomy/InlineEdit';
import AttributeCard from '@/components/taxonomy/AttributeCard';
import { AddAttributeForm } from '@/components/taxonomy/AddForms';
import DuplicateTemplate from '@/components/taxonomy/DuplicateTemplate';
import { Availability } from '@/pages/TaxonomyTemplates';
import { plural } from '@/components/taxonomy/counts';

function TemplateHeader({ t, save, onDuplicate }) {
  const [desc, setDesc] = useState(t.description || '');
  useEffect(() => { setDesc(t.description || ''); }, [t.description]);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 sm:p-6 space-y-4" data-testid="template-header">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <InlineEdit value={t.name} canEdit onSave={(name) => save('', 'PUT', { name })} className="text-2xl font-bold text-[#0F172A] font-['Montserrat']" testId="template-name" />
          <Availability t={t} />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#64748B]">{t.status === 'active' ? 'Active' : 'Inactive'}
            <Switch checked={t.status === 'active'} onCheckedChange={(v) => save('', 'PUT', { status: v ? 'active' : 'inactive' })} data-testid="template-status" />
          </label>
          <DuplicateTemplate template={t} onDuplicate={onDuplicate} testId="template-duplicate-btn" />
        </div>
      </div>
      <Input value={desc} maxLength={300} onChange={(e) => setDesc(e.target.value)} onBlur={() => desc.trim() !== (t.description || '') && save('', 'PUT', { description: desc.trim() })}
        placeholder="Short description shown to admins (optional)" data-testid="template-description" />
      <p className="text-xs text-[#475569] flex items-start gap-2 bg-[#F3F6FB] rounded-lg px-3 py-2" data-testid="template-isolation-note">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#1A3E6F]" />
        {plural(t.clients_using, 'client')} started from this template. Edits here only affect clients that choose it from now on — existing client copies never change.
      </p>
    </div>
  );
}

export default function TaxonomyTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);
  const base = `/platform/taxonomy-templates/${templateId}`;
  const load = useCallback(() => jsonApi(base).then(setT).catch(setError), [base]);
  useEffect(() => { load(); }, [load]);
  const save = async (path, method, body, msg) => {
    try { setT(await jsonApi(`${base}${path}`, method, body)); if (msg) toast.success(msg); return true; }
    catch (e) { toast.error(e.message); if (e.status === 409) load(); return false; }
  };
  const duplicate = async (id, name) => {
    try { const d = await jsonApi(`/platform/taxonomy-templates/${id}/duplicate`, 'POST', { name }); toast.success('Duplicated as an inactive draft — activate it when ready'); navigate(`/platform/taxonomy-templates/${d.template_id}`); return true; }
    catch (e) { toast.error(e.message); return false; }
  };
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="template-editor-page">
        <button type="button" onClick={() => navigate('/platform/taxonomy-templates')} className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#1A3E6F]" data-testid="template-back-btn"><ArrowLeft className="w-4 h-4" /> All templates</button>
        {error && <p className="text-sm text-[#A1272E] bg-[#FFF0F0] border border-[#F0C6C9] rounded-xl px-4 py-3" data-testid="template-editor-error">{error.message}</p>}
        {!t && !error && <div className="py-16 text-center"><div className="loading-spinner mx-auto" /></div>}
        {t && (
          <>
            <TemplateHeader t={t} save={save} onDuplicate={duplicate} />
            <AddAttributeForm onAdd={(b) => save('/attributes', 'POST', b, 'Attribute added')} />
            {!t.attributes.length && <p className="text-sm text-[#94A3B8] text-center py-8" data-testid="template-empty">No attributes yet. Add the first one above — the template becomes available to clients once it has an active factor.</p>}
            {t.attributes.map((a, i) => (
              <AttributeCard key={a.attribute_id} attr={{ ...a, factors: a.factors || [] }} index={i} canEdit
                inactiveNote="Inactive attributes and their factors are not copied to clients."
                onUpdateAttr={(id, b) => save(`/attributes/${id}`, 'PUT', b, 'Attribute updated')}
                onUpdateFactor={(id, b) => save(`/factors/${id}`, 'PUT', b, 'Factor updated')}
                onAddFactor={(b) => save('/factors', 'POST', b, 'Factor added')} />
            ))}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
